import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { deriveAnchoredAge } from '../utils/levelCalculations';
import { FaUser, FaList, FaBrain } from 'react-icons/fa';
import { DIMENSION_STYLES, UnifiedHeader, UnifiedLegend } from '../styles/DimensionStyles';
import { calculateFormProgress } from '../utils/gradeCalculationForWithdrawal';

// Definimos las interfaces localmente
interface Skill {
  id: number;
  name: string;
  levels: string[];
  selectedLevelIndex: number | null;
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
  notEvaluated?: boolean; // Marca si la habilidad no fue evaluada (no afecta el cálculo)
}

interface Student {
  id: number | string;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
  previousYearLevels?: {
    dimensionId: number;
    skillId: number;
    level: number | null;
    year: string;
  }[];
  qualitativeDescription?: string;
}

const LEVEL_COLORS = {
  expected: 'bg-yellow-50 border-2 border-yellow-400',    // Color amarillo claro para nivel esperado
  previous: 'bg-gray-50 border-2 border-gray-400',        // Color gris claro para nivel del año anterior
  selected: 'bg-blue-500 text-white shadow-md',           // Color azul para nivel seleccionado
  default: 'bg-white hover:bg-blue-50 border border-gray-200', // Color por defecto
  notApplicable: 'bg-gray-50 text-gray-400 italic',      // Color para "No aplica" no seleccionado
  notApplicableSelected: 'bg-blue-500 text-white shadow-md italic', // Color para "No aplica" seleccionado
  previouslySelected: 'bg-blue-50 border border-blue-300', // Color para nivel previamente seleccionado
};

// Habilidades de Pensamiento (hardcodeadas)
const habilidadesPensamientoSkills: Skill[] = [
  {
    "id": 1,
    "name": "Identificar ideas clave",
    "levels": [
      "No Aplica",
      "No Aplica",
      "Identifica ideas importantes en actividades concretas.",
      "Identifica ideas importantes en textos leídos en voz alta.",
      "Identifica ideas importantes en textos leídos de manera personal.",
      "No Aplica",
      "Identifica idea principal de un texto",
      "Identifica ideas principales y secundarias de un texto",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 2,
    "name": "Clasificar según relevancia",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Clasifica información según relevancia",
      "Realiza mapas conceptuales de las ideas importantes",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 3,
    "name": "Extraer información explícita e implícita",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Extrae información explícita e implícita de un texto.",
      "Reconoce ideas importantes en un texto para identificar la idea principal.",
      "Identifica información explícita e implícita para reconocer las ideas importantes",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 4,
    "name": "Responder preguntas sobre lo escuchado",
    "levels": [
      "Responde preguntas explícitas de acuerdo a lo escuchado en situaciones cotidianas.",
      "Responde preguntas explícitas de acuerdo a lo escuchado en una lectura en voz alta o experiencia de aprendizaje particular.",
      "Responde preguntas explícitas e implícitas provenientes de situaciones cotidianas (elementos concretos, trabajo de sus compañeros, experiencias, narraciones)",
      "Responde preguntas abiertas las que argumenta según su experiencia personal o información adicional.",
      "Responde preguntas explícitas, implícitas y evaluativas de un texto oral o escrito.",
      "Responde preguntas explícitas, implícitas y evaluativas sobre textos literarios y no literarios.",
      "Responde preguntas explícitas, implícitas y evaluativas sobre textos literarios y no literarios, asociado a sus conocimientos previos",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 5,
    "name": "Formular preguntas",
    "levels": [
      "Realiza preguntas, de manera guiada, de acuerdo al contexto en el que se está trabajando.",
      "Realiza preguntas, de manera autónoma y espontánea, acerca de lo escuchado y/o algún tema trabajado.",
      "Formula preguntas claras y completas respecto a situaciones cotidianas (elementos concretos, trabajo de sus compañeros, experiencias, narraciones)",
      "Genera preguntas respecto a situaciones que participa o visualiza, reflexionando frente al hecho.",
      "Formula preguntas para obtener información adicional.",
      "Formula preguntas y es capaz de encontrar respuestas.",
      "No Aplica",
      "Formula preguntas para elaborar y/o aclarar ideas de los leído o conversado.",
      "Pregunta de manera textual e inferencial sobre textos o temas trabajados"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 6,
    "name": "Reconocer tipos de texto",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Reconoce diferentes tipos de textos a partir de su estructura.",
      "Reconoce diferentes tipos de textos a partir de su estructura, identificando su función.",
      "Diferencia tipos de textos según sus estructuras, identificando su función.",
      "Diferencia los tipos de textos entre literarios y no literarios, describiendo sus funciones.",
      "Diferencia textos literarios y no literarios, reconociendo el uso de imágenes, tablas, mapas, diagramas, lenguaje figurado, etc.",
      "Describe y utiliza textos literarios y no literarios, de acuerdo a sus características y el uso de sus partes y funciones."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 7,
    "name": "Identificar propósito del autor (oral)",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Identifica el propósito que tiene el autor de diversos textos narrados.",
      "Identifica el propósito del autor de distintos tipos de textos de manera oral.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 8,
    "name": "Identificar propósito del autor (escrito)",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Identifica el propósito que tiene el autor en textos de estructura conocida, leídos de manera individual.",
      "Identifica el propósito del autor al leer el texto.",
      "Infiere el propósito del texto a través de la opinión del autor.",
      "Identifica el mensaje del texto y a quién se dirige, según el autor.",
      "Reconoce el mensaje y propósito del texto leído."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 9,
    "name": "Explicar información entregada por el texto",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Explica qué información entregan algunos de los textos.",
      "Explica qué información entregan cada tipo de texto utilizado.",
      "Explica la información entregada por el autor, según el tipo de texto."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 10,
    "name": "Visualizar mentalmente el texto",
    "levels": [
      "Verbaliza o dibuja situaciones, personajes y ambientes a partir de diferentes sentidos.",
      "Verbaliza o dibuja su propia representación mental a partir de lo escuchado, describiendo situaciones, personajes o ambientes.",
      "Verbaliza y dibuja su propia representación mental a partir de lo escuchado, describiendo situaciones, personajes o ambientes.",
      "Visualiza lo que describe el texto (personajes, situaciones, ambiente) utilizando sus sentidos para recrear a través de dibujos y de manera oral, intencionando la escritura.",
      "Visualiza lo que describe el texto (personajes, situaciones, ambiente) utilizando sus sentidos para recrear a través de dibujos, texto escrito o de manera oral.",
      "Visualiza lo que describe el texto demostrando a través de dibujos, descripciones o narraciones.",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 11,
    "name": "Representar situaciones imaginadas",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Representa a través de dibujos o narración, diferentes situaciones imaginadas en el texto.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 12,
    "name": "Caracterizar personajes",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Caracteriza de manera física y psicológica a algunos personajes.",
      "Describe de manera escrita, la visualización de personajes de manera física y psicológica."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 13,
    "name": "Visualizar ambiente",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Describe de manera oral o escrita la visualización del ambiente de textos escuchados o leídos.",
      "No Aplica",
      "Describe de manera oral o escrita la visualización del ambiente de los textos leídos.",
      "Describe de manera escrita, la visualización del ambiente en los textos leídos."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 14,
    "name": "Monitorear comprensión",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Monitorea la propia comprensión de textos o situaciones de la vida diaria, a través de pautas, de manera independiente o grupal, reconociendo cuando el texto no le hace sentido.",
      "Monitorea su propia comprensión, ajustando su avance a lograr el propósito de la lectura, revisando o mirando hacia atrás en la lectura.",
      "Monitorea su propia comprensión, ajustando su avance a lograr el propósito de la lectura, revisando o mirando hacia atrás en la lectura, haciéndose preguntas y buscando de manera autónoma las respuestas.",
      "No Aplica",
      "Monitorea su propia comprensión, ajustando su avance a lograr el propósito de la lectura, revisando o mirando hacia atrás en la lectura, encontrando de manera autónoma las respuestas."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 15,
    "name": "Inferir información",
    "levels": [
      "No Aplica",
      "Intenta realizar inferencias a partir de información explícita y/o experiencias concretas.",
      "Infiere en situaciones concretas, vinculando lo observado a sus conocimientos previos",
      "Infiere por medio de pistas observadas en situaciones, textos narrados e imágenes, usando conocimientos previos.",
      "Infiere información implícita en textos orales y escritos a partir de pistas del texto, usando conocimientos y experiencias previas.",
      "Infiere o alcanza conclusiones sobre lo que se ha leído y fundamenta con evidencia textual, conocimientos previos o evidencias concretas.",
      "No Aplica",
      "Infiere en su lectura a través de predicciones y evidencias, relacionándolo a su experiencia.",
      "Infiere y desarrolla conclusiones sobre lo que ha leído, fundamentando con experiencia y evidencia textual."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 16,
    "name": "Recontar historias",
    "levels": [
      "Recuenta una historia basándose y apoyándose en las imágenes del relato, identificando sucesos importantes.",
      "Recuenta una historia, identificando sucesos importantes (basándose y apoyándose en las imágenes del relato si es necesario).",
      "Recuenta, recordando acontecimientos importantes en el orden correspondiente.",
      "Recuenta acontecimientos importantes de una experiencia, narración o texto leído.",
      "Recuenta reconstruyendo la secuencia de las acciones en el orden correspondiente a través de esquemas (mapa conceptual, línea de tiempo) de experiencias personales.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 17,
    "name": "Ordenar cronológicamente",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Ordena cronológicamente hechos que sucedieron en una narración reconociendo aquellos acontecimientos más importantes.",
      "Recuenta reconstruyendo la secuencia de las acciones en el orden correspondiente a través de esquemas (mapa conceptual, línea de tiempo) en textos narrativos.",
      "Resume un texto escuchado mediante un esquema.",
      "Resume un texto leído a través de un esquema.",
      "Resume un texto leído a través de su escritura.",
      "Resume un texto leído a través de su escritura, imágenes u otro diseño."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 18,
    "name": "Resumir experiencias",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Resume experiencias personales mediante el relato oral.",
      "Resume experiencias personales mediante el relato oral y escrito, identificando las ideas principales.",
      "No Aplica",
      "Resume experiencias personales mediante escritura, sintetizando la información.",
      "Sintetiza información escuchada para tener registros de información que le ha interesado.",
      "Sintetiza textos para tener registros de información que le ha interesado."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 19,
    "name": "Interpretar lenguaje figurado",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Interpreta lenguaje figurado simple y refranes comunes en situaciones cotidianas.",
      "Interpreta lenguaje figurado simple y de la vida cotidiana, en un texto oral o escrito.",
      "No Aplica",
      "Interpreta el lenguaje figurado utilizado en un texto.",
      "Explica el lenguaje figurado identificando sentidos, estados de ánimo u otras interpretaciones.",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 20,
    "name": "Identificar figuras literarias",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Identifica algunos lenguajes figurados en la vida cotidiana como la comparación o la personificación.",
      "Identifica la comparación, la personificación y la metáfora en conversaciones o textos leídos.",
      "Reconoce la personificación, comparación y metáforas, explicando su representación.",
      "Reconoce la personificación, comparación y metáforas y otras figuras literarias, explicando su representación."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 21,
    "name": "Interpretar expresiones implícitas",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Interpreta expresiones que no están de manera directa en el texto."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 22,
    "name": "Comparar información oral",
    "levels": [
      "Compara información en conjunto con el adulto, utilizando conocimientos previos y material concreto, imágenes o a través del relato oral.",
      "Compara información, utilizando conocimientos previos y material concreto, imágenes o a través del relato oral.",
      "Comparar información, situaciones, personajes, ambientes, objetos concretos, sonidos y movimientos utilizando conocimientos previos y experiencias buscando similitudes y/o diferencias.",
      "No Aplica",
      "Compara situaciones, personajes o ambientes opuestos en narraciones orales o textos leídos personalmente.",
      "No Aplica",
      "Compara situaciones, personajes o ambientes opuestos en textos leídos personalmente.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 23,
    "name": "Comparar información escrita",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Compara información en el texto utilizando conocimientos previos y experiencias.",
      "Compara la información entregada en diferentes textos sobre la misma información.",
      "Compara diferentes tipos de texto y sus propósitos.",
      "Compara la información leída en diferentes textos sobre la misma información.",
      "Compara textos de diferentes autores y elige preferencias por alguno justificando su elección.",
      "Compara la información dentro del mismo texto."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 24,
    "name": "Opinar y argumentar",
    "levels": [
      "Opina sobre lo escuchado, visto y sentido.",
      "Opinar sobre lo escuchado, vinculándolo a su vida.",
      "Opina sobre lo escuchado, vinculándolo a su vida, entregando al menos un argumento claro.",
      "Opina sobre lo escuchado y leído, vinculándolo a su vida, dando al menos un argumento claro.",
      "Opina sobre lo escuchado y leído, de manera escrita, dando argumentos claros y asociados a sus conocimientos previos y al texto o contexto trabajado.",
      "Expresa opiniones del texto leído, a través de una escritura.",
      "No Aplica",
      "Explica y opina sobre actitudes y reacciones de personajes, fundamentando sus argumentos.",
      "Formula y fundamenta su opinión de un tema sobre distintos textos leídos."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 25,
    "name": "Diferenciar hecho y opinión",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Identifica un hecho y una opinión.",
      "Identifica y diferencia entre un hecho y una opinión utilizando información entregada en el texto.",
      "Expresa sobre la lectura realizada, identificando hechos y opiniones.",
      "Diferencia entre opiniones y hechos, reconociéndolo en los textos que lee.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 26,
    "name": "Predecir en situaciones cotidianas",
    "levels": [
      "Predice en situaciones cotidianas.",
      "No Aplica",
      "No Aplica",
      "Predice en situaciones cotidianas, utilizando diferentes elementos, incluyendo conocimientos previos, para dar su predicción",
      "No Aplica",
      "No Aplica",
      "Predice y fundamenta sus predicciones utilizando intencionadamente nuevo vocabulario, estructuras del texto y patrones familiares de la narrativa.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 27,
    "name": "Predecir a partir de imágenes",
    "levels": [
      "Predice de qué se pueden tratar diferentes textos a través de las imágenes que se presenten.",
      "Predice situaciones que pueden suceder a través de la observación de imágenes y lecturas en voz alta.",
      "Predice situaciones que pueden suceder durante las narraciones orales",
      "Predice contenido, utilizando títulos, encabezado, ilustraciones u otros elementos que entregan contexto.",
      "Predice contenido, eventos y soluciones utilizando títulos, encabezado, ilustraciones y temas de cuentos.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 28,
    "name": "Reconocer causa-efecto en narraciones",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Reconoce causa-efecto de acciones que ocurren en una narración oral.",
      "Reconoce causas y efectos en un texto leído de manera independiente.",
      "Reconoce causas y efectos que se producen en acciones de un texto leído.",
      "Reconoce algunas causas que producen ciertas acciones o efectos, al igual que relaciona los efectos que producen ciertas acciones, por ejemplo de los personajes de un texto leído.",
      "No Aplica",
      "Reconoce las causas que producen ciertos efectos, al igual que relaciona los efectos que producen ciertas acciones, por ejemplo de los personajes en un texto leído."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 29,
    "name": "Reconocer causas en la vida diaria",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Reconoce causa-efecto que producen acciones en la vida cotidiana.",
      "Reflexiona sobre causas y consecuencias de actos.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 30,
    "name": "Ejemplificar consecuencias",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Ejemplifica con acciones en la vida diaria que producen una determinada consecuencia.",
      "No Aplica",
      "Ejemplifica y fundamenta acciones en la vida diaria que producen una determinada consecuencia.",
      "No Aplica",
      "Ejemplifica y fundamenta acciones (comunidad, país, mundo) que producen una determinada consecuencia.",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 31,
    "name": "Opinar sobre situaciones cotidianas",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Formula una opinión sobre situaciones de la vida cotidiana.",
      "Formula una opinión sobre el texto escuchado y situaciones cotidianas, utilizando información que respalda y conocimientos previos.",
      "No Aplica",
      "Expresa la opinión sobre diferentes textos leídos.",
      "Expresa y defiende razonablemente una opinión en relación a un texto leído y/o su vida personal.",
      "Expresa y defiende una opinión, dando razones encontradas en textos y vivido de manera personal."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 32,
    "name": "Fundamentar opinión",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Respalda su opinión ocupando la frase “sí porque / no porque”, identificando alguna idea particular.",
      "Respalda su opinión ocupando la palabra “porque”, entregando razones simples para defender su postura.",
      "No Aplica",
      "Fundamenta su opinión con información de textos leídos, entregando algunas razones.",
      "Da varias razones, claras y coherentes para afirmar su expresión y opinión.",
      "Da razones, claras y coherentes para argumentar en profundidad su expresión y opinión."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 33,
    "name": "Establecer conexiones con la experiencia",
    "levels": [
      "No Aplica",
      "Establece conexiones entre cierta información con sus propias experiencias.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Compara elementos presentes en un texto con elementos de su mundo.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 34,
    "name": "Dramatizar narraciones",
    "levels": [
      "No Aplica",
      "Recrea y dramatiza narraciones orales, acorde a lo escuchado o conversado.",
      "Recrea y dramatiza narraciones orales, representando la situación según su idea principal.",
      "Recrea personajes por medio de expresiones artísticas, dramatizando narraciones y situaciones escritas",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 35,
    "name": "Planificar narraciones",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Planifica una narración con contenido específico trabajado con anterioridad",
      "Escribe y representa una narración asociada a una lectura o trabajo realizado con anterioridad.",
      "No Aplica",
      "Redacta una narración o texto claro, que se asocia a la información leída.",
      "Registra ideas para realizar una investigación mediante experiencias nuevas y previas.",
      "Extrae y registra información relevante de diferentes textos leídos y experiencias vividas."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 36,
    "name": "Evaluar situaciones",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Evalúa situaciones entregadas en una lectura oral.",
      "Evalúa situaciones entregadas en una lectura oral y en situaciones cotidianas, entregando una opinión.",
      "Evalúa situaciones entregadas en una lectura oral o en la vida cotidiana, reconociendo las consecuencias y acciones que se tomaron.",
      "No Aplica",
      "Evalúa algunos textos o lecturas orales, para utilizarlos como información y responder cierta información.",
      "No Aplica",
      "Evalúa si un texto entrega la suficiente información para responder determinadas preguntas."
    ].slice(0,9),
    "selectedLevelIndex": null
  },
  {
    "id": 37,
    "name": "Recomendar textos",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Evalúa textos leídos, para promover una recomendación.",
      "Recomienda a otros compañeros textos leídos o situaciones vividas adecuadas, que les haya gustado.",
      "Recomienda a otros compañeros textos leídos, dado razones del por qué.",
      "No Aplica",
      "Recomienda a otras personas algunos textos que ha leído y lo asocia a los gustos de otros.",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 38,
    "name": "Modificar narraciones",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Cambia un final o modifica una parte de un texto o situación.",
      "No Aplica",
      "Modifica acciones de una narración, para generar nuevas historias o posibilidades de cambio.",
      "No Aplica",
      "Modifica personajes o situaciones específicas, que pueden cambiar profundamente razones o acontecimientos."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 39,
    "name": "Comunicar preferencias",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Comunica de manera clara sus preferencias, planificando sus narraciones.",
      "No Aplica",
      "Comunica de manera escrita, a través de descripciones y opiniones, sobre otros textos",
      "Opina sobre distintos textos escuchados o leídos, identificando variados puntos de vista.",
      "Opina sobre distintos textos leídos, dando razones claras del por qué."
    ],
    "selectedLevelIndex": null
  }
]
;

// Definición de la dimensión con datos hardcodeados
export const habilidadesPensamientoDimension: Dimension = {
  id: 2,
  name: "Habilidades pensamiento",
  skills: habilidadesPensamientoSkills // Datos hardcodeados permanentes
};

export const HabilidadesPensamiento: React.FC<{
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number | null) => void;
  handleNotEvaluatedChange?: (studentId: number | string, dimensionId: number, skillId: number, notEvaluated: boolean) => void;
  viewMode: 'student' | 'skill';
  handleViewModeChange: (mode: 'student' | 'skill') => void;
  showInstructions: boolean;
  selectedCourse: string;
  setSelectedCourse: (course: string) => void;
  courses: string[];
  handleStudentSelect: (event: React.MouseEvent<HTMLButtonElement>, id: number | string) => void;
  getExpectedLevel: (age: number) => number;
  getPreviousYearLevel: (age: number) => number;
}> = ({
  students,
  selectedStudentIds,
  handleLevelChange,
  handleNotEvaluatedChange,
  viewMode,
  handleViewModeChange,
  showInstructions,
  selectedCourse,
  setSelectedCourse,
  courses,
  handleStudentSelect,
  getExpectedLevel,
  getPreviousYearLevel,
}) => {
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);
  const [showAllYears, setShowAllYears] = useState(true);
  
  // Estado para rastrear niveles previamente seleccionados
  const [previouslySelectedLevels, setPreviouslySelectedLevels] = useState<{[key: string]: number | null}>();

  // Función para filtrar estudiantes
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "lastName" | "course">("lastName");

  const getSkillLevel = (student: Student, dimensionId: number, skillId: number): number | null => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.level ?? null;
  };

  // 🚫 Función para verificar si una habilidad está marcada como "No evaluada"
  const isSkillNotEvaluated = (student: Student, dimensionId: number, skillId: number): boolean => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.notEvaluated === true;
  };

  // 🎯 NUEVO: Helper compartido para obtener el nivel del año anterior REAL
  const getHistoricalLevel = useCallback((student: Student, skillId: number, currentAge: number) => {
    if (student.previousYearLevels && student.previousYearLevels.length > 0) {
      const historical = student.previousYearLevels.find(
        pl => pl.dimensionId === habilidadesPensamientoDimension.id && pl.skillId === skillId
      );
      if (historical !== undefined && historical.level !== null) {
        return historical.level;
      }
    }
    return getPreviousYearLevel(currentAge);
  }, []);

  const getCompletionColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'bg-indigo-500 text-white border-indigo-600 shadow-md transform scale-105';
    }
    
    if (percentage >= 75) {
      return 'bg-green-50 text-green-800 border-green-300 hover:bg-green-100 hover:border-green-400';
    } else if (percentage >= 50) {
      return 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100 hover:border-blue-400';
    } else if (percentage >= 25) {
      return 'bg-yellow-50 text-yellow-800 border-yellow-300 hover:bg-yellow-100 hover:border-yellow-400';
    } else {
      return 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100 hover:border-red-400';
    }
  };

  const getPercentageTextColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'text-white opacity-75';
    }
    
    if (percentage >= 75) {
      return 'text-green-600 opacity-90';
    } else if (percentage >= 50) {
      return 'text-blue-600 opacity-90';
    } else if (percentage >= 25) {
      return 'text-yellow-600 opacity-90';
    } else {
      return 'text-red-600 opacity-90';
    }
  };

  // Función para extraer el primer apellido
  // Formato esperado: "Apellido Paterno; Apellido Materno; Nombre"
  const getFirstLastName = (fullName: string): string => {
    const nameParts = fullName.trim().split(' ');
    // El primer apellido (apellido paterno) es la primera parte del nombre
    if (nameParts.length >= 1) {
      return nameParts[0].toLowerCase();
    }
    // Si está vacío, retornamos la cadena vacía
    return '';
  };

  const handleLocalLevelChange = useCallback((studentId: number | string, skillId: number, levelIndex: number | null) => {
    const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();

    const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
    if (student) {
      const currentLevel = getSkillLevel(student, habilidadesPensamientoDimension.id, skillId);

      if (currentLevel !== null && !previouslySelectedLevels?.[`${studentId}-${skillId}`]) {
        setPreviouslySelectedLevels(prev => ({
          ...prev,
          [`${studentId}-${skillId}`]: currentLevel
        }));
      }

      // Lógica de toggle: si el nivel ya está seleccionado, deseleccionarlo
      if (currentLevel === levelIndex) {
        handleLevelChange(studentId, habilidadesPensamientoDimension.id, skillId, null);
        return;
      }
    }

    // Si levelIndex es -1, convertir a null (para compatibilidad con el botón de reiniciar)
    const actualLevelIndex = levelIndex === -1 ? null : levelIndex;
    handleLevelChange(studentId, habilidadesPensamientoDimension.id, skillId, actualLevelIndex);
  }, [handleLevelChange, students, previouslySelectedLevels]);

  const filteredStudents = useMemo(() => {
    const filtered = students.filter(student => 
      selectedCourse ? student.course === selectedCourse : true
    );
    
    let sorted = [...filtered];
    
    if (sortBy === "name") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "lastName") {
      sorted.sort((a, b) => {
        const lastNameA = getFirstLastName(a.name);
        const lastNameB = getFirstLastName(b.name);
        return lastNameA.localeCompare(lastNameB);
      });
    } else if (sortBy === "course") {
      sorted.sort((a, b) => a.course.localeCompare(b.course));
    }
    
    if (!searchTerm.trim()) return sorted;
    
    return sorted.filter(student => 
      student.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [students, selectedCourse, searchTerm, sortBy]);

  // Modificamos el useEffect para manejar la selección por defecto
  useEffect(() => {
    if (viewMode === 'student' && selectedStudentIds.length === 0 && students.length > 0) {
      // Usar el primer estudiante disponible del curso actual, no hardcodear
      const firstStudent = students[0];
      if (firstStudent) {
        handleStudentSelect(new MouseEvent('click') as any, firstStudent.id as any);
      }
    } else if (viewMode === 'skill' && !selectedSkillId && habilidadesPensamientoDimension.skills.length > 0) {
      const firstSkill = habilidadesPensamientoDimension.skills[0];
      if (firstSkill) {
        setSelectedSkillId(firstSkill.id);
      }
    }
  }, [viewMode, students, selectedStudentIds, handleStudentSelect]);

  // Nuevo useEffect para inicializar los niveles previamente seleccionados desde la base de datos
  useEffect(() => {
    if (students.length > 0) {
      // Inicializar los niveles previamente guardados en la base de datos
      const initialLevels: {[key: string]: number} = {};
      
      students.forEach(student => {
        student.skillLevels.forEach(skillLevel => {
          if (skillLevel.dimensionId === habilidadesPensamientoDimension.id && skillLevel.level !== null) {
            initialLevels[`${student.id}-${skillLevel.skillId}`] = skillLevel.level;
          }
        });
      });
      
      // Solo actualizar si hay datos que inicializar
      if (Object.keys(initialLevels).length > 0) {
        setPreviouslySelectedLevels(initialLevels);
      }
    }
  }, [students]); // Se ejecuta cuando cambia la lista de estudiantes

  const renderStudentView = useMemo(() => {
    return () => selectedStudentIds.map(studentId => {
    const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
    const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
    if (!student) return null;

    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

    const expectedLevel = getExpectedLevel(age);

    const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
    
    const getVisibleYears = () => {
      if (showAllYears) {
        return allYears;
      }
      
      const studentAge = age;
      const minAge = Math.max(4, studentAge - 2);
      const maxAge = Math.min(12, studentAge + 2);
      
      return allYears.filter((_, index) => {
        const yearAge = index + 4;
        return yearAge >= minAge && yearAge <= maxAge;
      });
    };
    
    const visibleYears = getVisibleYears();
    const visibleIndices = visibleYears.map((year, index) => {
      const match = year.match(/\d+/);
      const yearNum = match ? parseInt(match[0]) : 4;
      const calculatedIndex = yearNum - 4;
      // Asegurar que nunca sea NaN y usar fallback con index
      return isNaN(calculatedIndex) ? index : calculatedIndex;
    });

    return (
      <div key={student.id} className="mb-8">
        <div className="overflow-x-auto">
          <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
            <div className="bg-gray-50 p-3 border-b border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Progreso de habilidades:</span>
                  <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                    {(() => {
                      // Usar cálculo correcto excluyendo "No aplica"
                      const progress = calculateFormProgress(
                        habilidadesPensamientoDimension.id, 
                        student.course, 
                        student.skillLevels
                      );
                      const progressPercent = progress.percentage;
                      
                      let barColor = "bg-red-500";
                      if (progressPercent >= 75) barColor = "bg-green-500";
                      else if (progressPercent >= 50) barColor = "bg-blue-500";
                      else if (progressPercent >= 25) barColor = "bg-yellow-500";
                      
                      return (
                        <div 
                          className={`h-2.5 rounded-full ${barColor}`} 
                          style={{ width: `${progressPercent}%` }}
                        ></div>
                      );
                    })()}
                  </div>
                  <span className="text-xs text-gray-500">
                    {(() => {
                      // Usar cálculo correcto excluyendo "No aplica"
                      const progress = calculateFormProgress(
                        habilidadesPensamientoDimension.id, 
                        student.course, 
                        student.skillLevels
                      );
                      return `${progress.completedSkills} de ${progress.totalValidSkills} habilidades`;
                    })()}
                  </span>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAllYears(!showAllYears)}
                    className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                      showAllYears 
                        ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                        : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                  </button>
                  
                  <button
                    onClick={() => {
                      if (window.confirm("¿Seguro que desea reiniciar todos los niveles de habilidad para este estudiante?")) {
                        habilidadesPensamientoDimension.skills.forEach(skill => {
                          // Pasar un valor para level que será interpretado como null
                          handleLocalLevelChange(student.id as any, skill.id, -1);
                        });
                      }
                    }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                  >
                    Reiniciar
                  </button>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">Habilidad</th>
                    {visibleYears.map((ageGroup, index) => {
                      const actualIndex = visibleIndices[index];
                      return (
                        <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="mb-1">{ageGroup}</span>
                            <button
                              onClick={() => {
                                habilidadesPensamientoDimension.skills.forEach(skill => {
                                  const currentLevel = getSkillLevel(student, habilidadesPensamientoDimension.id, skill.id);
                                  if (currentLevel === actualIndex) {
                                    handleLevelChange(student.id as any, habilidadesPensamientoDimension.id, skill.id, null);
                                  } else {
                                    handleLevelChange(student.id as any, habilidadesPensamientoDimension.id, skill.id, actualIndex);
                                  }
                                });
                              }}
                              className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                            >
                              Seleccionar todo
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {habilidadesPensamientoDimension.skills.map((skill: Skill, skillIndex: number) => {
                  const selectedLevel = getSkillLevel(student, habilidadesPensamientoDimension.id, skill.id);
                  const skillIsNotEvaluated = isSkillNotEvaluated(student, habilidadesPensamientoDimension.id, skill.id);
                  
                  const previousYearLevel = getHistoricalLevel(student, skill.id, age);

                  return (
                      <tr key={skill.id} className={`${skillIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                        <td className="py-3 px-6 text-sm font-medium text-gray-900">
                          <div className="flex items-start">
                            <span className="rounded-full bg-indigo-100 text-indigo-800 w-6 h-6 flex items-center justify-center mr-2 font-bold text-xs">
                              {skillIndex + 1}
                            </span>
                            <div className="flex-1">
                              {skill.name}
                              {selectedLevel !== null && !skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-green-600 font-medium">
                                  Nivel seleccionado: {selectedLevel + 1}
                                </div>
                              )}
                              {skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-orange-600 font-medium">
                                  🚫 No evaluada
                                </div>
                              )}
                              {/* Botón para marcar como No Evaluada - en línea separada */}
                              {handleNotEvaluatedChange && (
                                <div className="mt-2">
                                  <button
                                    onClick={() => handleNotEvaluatedChange(student.id, habilidadesPensamientoDimension.id, skill.id, !skillIsNotEvaluated)}
                                    className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                      skillIsNotEvaluated
                                        ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                        : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                    }`}
                                    title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                  >
                                    {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                          const level = skill.levels[levelIndex];
                          if (!level) return null;
                          
                          const isNotApplicable = level.toLowerCase().includes('no aplica');
                          const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                          
                          let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                          
                          const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                          const isExpected = levelIndex === expectedLevel;
                          const isPrevious = levelIndex === previousYearLevel;

                          // 1. Fondo según selección o estado base
                          if (isNotApplicable) {
                            if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.notApplicableSelected;
                            } else {
                              buttonClass += LEVEL_COLORS.notApplicable;
                            }
                          } else if (selectedLevel === levelIndex) {
                            buttonClass += LEVEL_COLORS.selected;
                          } else if (previouslySelected) {
                            buttonClass += LEVEL_COLORS.previouslySelected;
                          } else {
                            buttonClass += LEVEL_COLORS.default;
                          }

                          // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                          let extraStyles: React.CSSProperties = {
                            boxSizing: 'border-box'
                          };

                          if (isPrevious) {
                            // Aplicar borde gris grueso para nivel año anterior
                            extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                            if (selectedLevel !== levelIndex) {
                              extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                            }
                          }

                          if (isExpected) {
                            // Si es esperado, aplicar borde amarillo
                            if (!isPrevious) {
                              extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                              }
                            }
                          }

                          return (
                            <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                              <button
                                onClick={() => handleLocalLevelChange(student.id as any, skill.id, levelIndex)}
                                className={buttonClass}
                                style={extraStyles}
                                title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                              >
                                <span className="text-center leading-tight">{level}</span>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Controles de navegación rápida - Nuevo */}
            <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
              </div>
              <div className="flex gap-2">
                <button 
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  Volver arriba
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
    });
  }, [selectedStudentIds, students, handleLocalLevelChange, getExpectedLevel, getPreviousYearLevel, showAllYears]);

  const renderSkillView = useMemo(() => {
    return () => {
    if (!selectedSkillId) return null;
    
    const skill = habilidadesPensamientoDimension.skills.find(s => s.id === selectedSkillId);
    if (!skill) return null;

    const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
    
    const getVisibleYears = () => {
      if (showAllYears) {
        return allYears;
      }
      
      const averageAge = filteredStudents.length > 0 ? 
        filteredStudents.reduce((sum, student) => {
          const a = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
          return sum + a;
        }, 0) / filteredStudents.length : 6;
      
      const minAge = Math.max(4, Math.floor(averageAge) - 2);
      const maxAge = Math.min(12, Math.floor(averageAge) + 2);
      
      return allYears.filter((_, index) => {
        const yearAge = index + 4;
        return yearAge >= minAge && yearAge <= maxAge;
      });
    };
    
    const visibleYears = getVisibleYears();
    const visibleIndices = visibleYears.map((year, index) => {
      const match = year.match(/\d+/);
      const yearNum = match ? parseInt(match[0]) : 4;
      const calculatedIndex = yearNum - 4;
      // Asegurar que nunca sea NaN y usar fallback con index
      return isNaN(calculatedIndex) ? index : calculatedIndex;
    });

    // Permite marcar o desmarcar masivamente la habilidad como "No evaluada" para los estudiantes filtrados
    const allStudentsNotEvaluated = filteredStudents.length > 0 && filteredStudents.every(student => 
      isSkillNotEvaluated(student, habilidadesPensamientoDimension.id, skill.id)
    );

    const toggleNotEvaluatedForAll = () => {
      if (!handleNotEvaluatedChange) return;
      
      const nextState = !allStudentsNotEvaluated;
      filteredStudents.forEach(student => {
        handleNotEvaluatedChange(student.id, habilidadesPensamientoDimension.id, selectedSkillId, nextState);
      });
    };

    return (
      <div className="mb-8">
        <div className="overflow-x-auto">
          <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
            <div className="bg-gray-50 p-3 border-b border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Progreso de la habilidad:</span>
                  <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                    {(() => {
                      const totalStudents = filteredStudents.length;
                      const completedStudents = filteredStudents.filter(student => 
                        getSkillLevel(student, habilidadesPensamientoDimension.id, skill.id) !== null
                      ).length;
                      const progressPercent = totalStudents > 0 ? (completedStudents / totalStudents) * 100 : 0;
                      
                      let barColor = "bg-red-500";
                      if (progressPercent >= 75) barColor = "bg-green-500";
                      else if (progressPercent >= 50) barColor = "bg-blue-500";
                      else if (progressPercent >= 25) barColor = "bg-yellow-500";
                      
                      return (
                        <div 
                          className={`h-2.5 rounded-full ${barColor}`} 
                          style={{ width: `${progressPercent}%` }}
                        ></div>
                      );
                    })()}
                  </div>
                  <span className="text-xs text-gray-500">
                    {filteredStudents.filter(student => 
                      getSkillLevel(student, habilidadesPensamientoDimension.id, skill.id) !== null
                    ).length} de {filteredStudents.length} estudiantes
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{skill.name}</span>
                  <button
                    onClick={() => setShowAllYears(!showAllYears)}
                    className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                      showAllYears 
                        ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                        : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">
                      <div className="flex flex-col items-start gap-1">
                        <span>Estudiante</span>
                        {handleNotEvaluatedChange && (
                          <button
                            onClick={toggleNotEvaluatedForAll}
                            className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all border ${
                              allStudentsNotEvaluated
                                ? 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200'
                                : 'bg-white text-indigo-900 border-white/50 hover:bg-indigo-50'
                            }`}
                            title="Marcar esta habilidad como no evaluada para todos los estudiantes filtrados"
                          >
                            {allStudentsNotEvaluated ? '✓ Habilitar evaluación' : '🚫 No evaluar esta habilidad'}
                          </button>
                        )}
                      </div>
                    </th>
                    {visibleYears.map((ageGroup, index) => {
                      const actualIndex = visibleIndices[index];
                      return (
                        <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="mb-1">{ageGroup}</span>
                            <button
                              onClick={() => {
                                filteredStudents.forEach(student => {
                                  const currentLevel = getSkillLevel(student, habilidadesPensamientoDimension.id, selectedSkillId);
                                    if (currentLevel === actualIndex) {
                                      handleLevelChange(student.id as any, habilidadesPensamientoDimension.id, selectedSkillId, null);
                                    } else {
                                      handleLevelChange(student.id as any, habilidadesPensamientoDimension.id, selectedSkillId, actualIndex);
                                    }
                                });
                              }}
                              className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                            >
                              Seleccionar todo
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student, studentIndex) => {
                    const selectedLevel = getSkillLevel(student, habilidadesPensamientoDimension.id, selectedSkillId);
                    const skillIsNotEvaluated = isSkillNotEvaluated(student, habilidadesPensamientoDimension.id, selectedSkillId);
                    
                    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
                    
                    return (
                      <tr key={student.id} className={`${studentIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                        <td className="py-3 px-6 text-sm font-medium text-gray-900">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-2">
                              <span className="text-indigo-800 font-bold text-xs">{student.name.split(' ')[0][0]}{student.name.split(' ')[1]?.[0]}</span>
                            </div>
                            <div className="ml-1 flex-1">
                              <div className="font-medium">{student.name}</div>
                              <div className="text-xs text-gray-500">{student.course}</div>
                              {selectedLevel !== null && !skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-green-600 font-medium">
                                  Nivel seleccionado: {selectedLevel + 1}
                                </div>
                              )}
                              {skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-orange-600 font-medium">
                                  🚫 No evaluada
                                </div>
                              )}
                              {/* Botón para marcar como No Evaluada - en línea separada */}
                              {handleNotEvaluatedChange && (
                                <div className="mt-2">
                                  <button
                                    onClick={() => handleNotEvaluatedChange(student.id, habilidadesPensamientoDimension.id, selectedSkillId, !skillIsNotEvaluated)}
                                    className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                      skillIsNotEvaluated
                                        ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                        : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                    }`}
                                    title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                  >
                                    {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                          const level = skill.levels[levelIndex];
                          if (!level) return null;
                          
                          const isNotApplicable = level.toLowerCase().includes('no aplica');
                            const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                            
                            let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                            
                            const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                            const expectedLevel = getExpectedLevel(age);

                          if (isNotApplicable) {
                            if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.notApplicableSelected;
                            } else {
                              buttonClass += LEVEL_COLORS.notApplicable;
                            }
                          } else if (selectedLevel === levelIndex) {
                            buttonClass += LEVEL_COLORS.selected;
                          } else if (previouslySelected) {
                            buttonClass += LEVEL_COLORS.previouslySelected;
                          } else if (levelIndex === expectedLevel) {
                            buttonClass += LEVEL_COLORS.expected;
                          } else if (levelIndex === previousYearLevel) {
                            buttonClass += LEVEL_COLORS.previous;
                          } else {
                            buttonClass += LEVEL_COLORS.default;
                          }

                          return (
                            <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                              <button
                                onClick={() => handleLocalLevelChange(student.id as any, selectedSkillId, levelIndex)}
                                className={buttonClass}
                                title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}`}
                              >
                                <span className="text-center leading-tight">{level}</span>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Controles de navegación rápida - Nuevo */}
            <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
              </div>
              <div className="flex gap-2">
                <button 
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  Volver arriba
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
    };
  }, [selectedSkillId, filteredStudents, showAllYears, handleLevelChange, previouslySelectedLevels, getExpectedLevel, getPreviousYearLevel, handleLocalLevelChange]);

  const renderSelectionButtons = () => {
    if (viewMode === 'student') {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Estudiante:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda en el centro */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar estudiante..." 
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Apellido</option>
                  <option value="course">Curso</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {filteredStudents.length === 0 ? (
              <div className="col-span-full text-center py-4 text-gray-500">
              No se encontraron estudiantes con "{searchTerm}"
            </div>
          ) : (
              filteredStudents.map((student) => {
                // ✅ CORREGIDO: Usar cálculo correcto excluyendo "No aplica"
                const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
                const progress = calculateFormProgress(
                  habilidadesPensamientoDimension.id, 
                  student.course, 
                  student.skillLevels
                );
                const percentage = progress.percentage;
                const isSelected = student.id && selectedStudentIds.some(sel => normalizeId(sel) === normalizeId(student.id));
                
                return (
                <button
                  key={student.id}
                  onClick={(event) => handleStudentSelect(event, student.id as any)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                    getCompletionColor(percentage, isSelected)
                  }`}
                  >
                    <div className="font-semibold" title={`${student.name} - ${student.course}`}>
                  {student.name}
            </div>
                  <div className={`text-xs font-medium ${getPercentageTextColor(percentage, isSelected)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
                  </button>
                );
              })
            )}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥75% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥50% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥25% completado,</span>
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
                <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                  Tip: Ctrl + clic para seleccionar múltiples estudiantes
                </span>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Habilidad:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar habilidad..." 
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'course' | 'lastName')}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Número</option>
                  <option value="course">% Completado</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {habilidadesPensamientoDimension.skills
              .filter(skill => 
                searchTerm === '' || 
                skill.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((skill) => {
                const completed = filteredStudents.filter(student => 
                  getSkillLevel(student, habilidadesPensamientoDimension.id, skill.id) !== null
                ).length;
                const percentage = filteredStudents.length > 0 ? (completed / filteredStudents.length) * 100 : 0;
                
                return (
              <button
                key={skill.id}
                onClick={() => setSelectedSkillId(skill.id)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, selectedSkillId === skill.id)
                }`}
              >
                    <div className="font-semibold" title={skill.name}>
                {skill.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, selectedSkillId === skill.id)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
              </button>
                );
              })}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥75% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥50% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥25% completado,</span>
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
                <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                Tip: Selecciona una habilidad para ver detalles
                </span>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderDataInputView = useMemo(() => {
    return () => (
      <div>
        {viewMode === 'student' ? renderStudentView() : renderSkillView()}
      </div>
    );
  }, [viewMode, renderStudentView, renderSkillView]);

      if (habilidadesPensamientoDimension.skills.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando habilidades de pensamiento...</p>
        </div>
      </div>
    );
  }

  if (habilidadesPensamientoDimension.skills.length === 0) {
    return (
      <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
        <p className="font-bold">Advertencia</p>
        <p>No se pudieron cargar las habilidades desde el archivo CSV. Contacte al administrador.</p>
      </div>
    );
  }

  return (
    <div className={DIMENSION_STYLES.container}>
      <UnifiedHeader
        dimensionName={habilidadesPensamientoDimension.name}
        icon={FaBrain}
        skillsCount={habilidadesPensamientoDimension.skills.length}
        selectedCourse={selectedCourse}
        courses={courses}
        onCourseChange={setSelectedCourse}
      />
        
      {/* Botones de modo de vista */}
      <div className="mt-3">
        <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-md">
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => handleViewModeChange('student')}
              className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === 'student' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaUser className="mr-1.5 text-sm" />
              <span className="font-medium">Por Estudiante</span>
            </button>
            
            <button
              onClick={() => handleViewModeChange('skill')}
              className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === 'skill' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaList className="mr-1.5 text-sm" />
              <span className="font-medium">Por Habilidad</span>
            </button>
          </div>
        </div>
      </div>
      
      {renderSelectionButtons()}
      
      <div className="bg-white p-4 rounded-lg shadow-md">
        {renderDataInputView()}
        
        {/* Leyenda de colores para los niveles de las tablas */}
        {(viewMode === 'student' && selectedStudentIds.length > 0) || (viewMode === 'skill' && selectedSkillId !== null) ? (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🎯</span>
              Leyenda de colores para niveles de desarrollo:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded mr-2 shadow-sm border"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel seleccionado</span> - Nivel evaluado para el estudiante
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-yellow-50 border-2 border-yellow-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel esperado</span> - Nivel correspondiente a la edad
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 border-2 border-gray-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel año anterior</span> - Nivel del año previo
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-50 border border-blue-300 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Previamente seleccionado</span> - Nivel anterior guardado
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-white border border-gray-200 rounded mr-2 hover:bg-blue-50"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Sin seleccionar</span> - Nivel disponible para evaluar
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 rounded mr-2 border flex items-center justify-center">
                  <span className="text-xs text-gray-400 italic">N/A</span>
                </div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">No aplica</span> - No corresponde para esta edad
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-orange-100 rounded mr-2 border border-orange-300 flex items-center justify-center">
                  <span className="text-xs text-orange-600">🚫</span>
                </div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">No evaluada</span> - No afecta el cálculo de nivel de logro
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-300">
              <p className="text-xs text-gray-500 italic">
                💡 <strong>Tip:</strong> Los colores ayudan a identificar rápidamente el progreso. Use "No evaluada" para habilidades que no pudieron ser evaluadas temporalmente.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

